/**
 * Sharing backend configuration (Brief 008 — level sharing, steps 1–2).
 *
 * The front-end ships to itch.io (static, cross-origin) and talks to a level
 * store on the KVM 2 VPS. API base + transport mode are build-time Vite env, so
 * the SAME code runs against a localStorage mock, a localhost dev server, or the
 * production VPS with no source change:
 *
 *   VITE_SHARING_API   absolute API origin, e.g. "https://api.example.com"
 *                      ('' / unset → no real backend)
 *   VITE_SHARING_MODE  'http' | 'mock'  (default: 'http' when API set, else 'mock')
 *
 * `mock` is a faithful localStorage stand-in (see MockLevelService) so the
 * Publish/Browse UI can be built and tested with ZERO infrastructure — the VPS is
 * the last step, not the first. Flip to a real base to go live.
 */
export type SharingMode = 'http' | 'mock';

const RAW_BASE = (import.meta.env.VITE_SHARING_API ?? '').trim().replace(/\/+$/, '');

/** Configured API origin (no trailing slash), or '' when unset. */
export const SHARING_API_BASE = RAW_BASE;

/** Active transport. Real backend when forced to 'http' or when a base is set. */
export const SHARING_MODE: SharingMode =
  (import.meta.env.VITE_SHARING_MODE as SharingMode | undefined) ?? (RAW_BASE ? 'http' : 'mock');

/** REST root for http-mode calls. */
export const API_ROOT = `${SHARING_API_BASE}/api`;

/**
 * Hard limits MIRRORED on the server (see docs/SHARING_API.md). Enforced
 * client-side too so a bad publish fails fast with a clear message instead of a
 * round-trip 400/413.
 */
export const LIMITS = {
  /** Max serialized level payload the server accepts (bytes). */
  maxLevelBytes: 256 * 1024,
  /** Display-string caps. Server truncates/rejects; client trims before send. */
  maxNameLen: 60,
  maxAuthorLen: 40,
  /** Max thumbnail data-URL the server accepts (bytes). */
  maxThumbBytes: 96 * 1024,
  /** Tier 2 (Feature F): max death cells accepted per `submitDeaths` batch. Bounds the
   *  best-effort analytics payload; the server independently caps too. */
  maxDeathsPerSubmit: 256,
} as const;

/** Per-request network timeout (ms) for http transport. */
export const REQUEST_TIMEOUT_MS = 12_000;
